/**
 * OpenAPI 자동생성 팩토리를 감싸는 싱글턴 API 클라이언트.
 *
 * 자동생성 코드(`base.ts`)의 `BASE_PATH`는 `http://127.0.0.1:8080`으로 하드코딩되어 있다.
 * 이 값을 그대로 쓰면 dev/prod 환경 분기가 불가능하므로, `getApiBaseUrl()`로 런타임에
 * 호스트명을 읽어 baseURL을 결정한다. 자동생성 팩토리 함수를 `APIClient`를 거치지 않고
 * 직접 호출하면 `BASE_PATH`(127.0.0.1)로 요청이 나간다.
 *
 * `withCredentials: true`이므로 BE CORS 설정에 `Access-Control-Allow-Origin: *`를 쓸 수 없다.
 * 명시적 origin 등록이 필요하다(user-api `cors-allow-domains` yml).
 *
 * ★ `pages/api/axiosInstance`와의 차이:
 * axiosInstance는 401 응답 시 토큰 자동 갱신(refresh) 인터셉터를 포함한다.
 * 이 클라이언트(`APIClient`)에는 갱신 인터셉터가 없으므로 토큰 만료 시 401이 그대로 전파된다.
 * 토큰 갱신이 필요한 호출(세션·민감 작업)은 axiosInstance를 쓰고,
 * 자동생성 스펙 기반 단순 호출은 `APIClient`를 쓴다.
 */
import axios from "axios";
import { Configuration } from "./configuration";
import { HealthControllerApiFactory, PaymentsApiFactory, TestApiFactory, UserControllerApiFactory } from "./api";
import { getApiBaseUrl } from "@/utils/getApiBaseUrl";

/** 런타임에 결정된 user-api 서버 baseURL. 자동생성 `BASE_PATH` 대신 이 값을 사용한다. */
export const API_ADDRESS = getApiBaseUrl();

class APIClientClass {
    public client = axios.create({
        baseURL: API_ADDRESS,
        withCredentials: true,
        timeout: 10000,
    })
    private readonly configuration = new Configuration();

    constructor() {
        // sessionStorage에 토큰이 있으면 모든 요청에 Authorization 헤더를 부착한다.
        this.client.interceptors.request.use((config) => {
            const token = sessionStorage.getItem("accessToken");

            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }

            return config;
        });
    }

    readonly Payments = PaymentsApiFactory(
        this.configuration,
        API_ADDRESS,
        this.client
    )
    readonly Test = TestApiFactory(
        this.configuration,
        API_ADDRESS,
        this.client
    )
    readonly User = UserControllerApiFactory(
        this.configuration,
        API_ADDRESS,
        this.client
    )
    readonly Health = HealthControllerApiFactory(
        this.configuration,
        API_ADDRESS,
        this.client
    )
}

export const APIClient = new APIClientClass()